import { env } from '@shared/config/env';

/**
 * Lazy loader for the Google Maps JavaScript API — the only way to get a REAL slippy map
 * (continuously streamed tiles) in the location picker. The Static Maps image it replaces can
 * never pan smoothly: it is one finite PNG, so dragging exposes blank edges and every release
 * refetches the whole image.
 *
 * Resolves `null` (never rejects) when the API can't be used — no browser key, a blocked/failed
 * script, or the Maps JavaScript API not enabled on the key. Callers fall back to the static map,
 * so the picker keeps working exactly as before in those cases.
 *
 * NOTE: unlike the Places/Static REST calls, this bootstrap cannot go through the `/maps-api` dev
 * proxy — the script pulls further resources straight from Google with the key embedded — so it
 * only runs when `VITE_GOOGLE_MAPS_API_KEY` is set.
 */

/** The slice of `google.maps` this app uses — declared locally so no `@types/google.maps` dep. */
export interface GoogleLatLng {
  lat(): number;
  lng(): number;
}

export interface GoogleMapsListener {
  remove(): void;
}

export interface GoogleMap {
  setCenter(position: { lat: number; lng: number }): void;
  setZoom(zoom: number): void;
  getCenter(): GoogleLatLng | undefined;
  getZoom(): number | undefined;
  addListener(eventName: string, handler: () => void): GoogleMapsListener;
}

export interface GoogleMapsApi {
  Map: new (container: HTMLElement, options: Record<string, unknown>) => GoogleMap;
}

interface MapsGlobal {
  google?: { maps?: GoogleMapsApi };
  [callbackName: string]: unknown;
}

/** Global callback name the bootstrap script invokes once `google.maps` is ready. */
const CALLBACK = '__rilomaGoogleMapsReady';
/** A script that never loads (offline, blocked by an extension/CSP) must not hang the picker. */
const LOAD_TIMEOUT_MS = 10_000;

/** Single in-flight/settled load shared by every caller — the script is injected at most once. */
let pending: Promise<GoogleMapsApi | null> | null = null;

export function loadGoogleMaps(): Promise<GoogleMapsApi | null> {
  pending ??= new Promise<GoogleMapsApi | null>((resolve) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      resolve(null);
      return;
    }
    const globals = window as unknown as MapsGlobal;
    const already = globals.google?.maps;
    if (already) {
      resolve(already);
      return;
    }
    const key = env.VITE_GOOGLE_MAPS_API_KEY;
    if (!key) {
      resolve(null); // dev-proxy mode: the REST calls work, the JS bootstrap can't
      return;
    }

    let settled = false;
    const finish = (api: GoogleMapsApi | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      globals[CALLBACK] = undefined; // release the bootstrap callback (not `delete` — lint rule)
      resolve(api);
    };
    const timer = setTimeout(() => finish(null), LOAD_TIMEOUT_MS);

    globals[CALLBACK] = () => finish(globals.google?.maps ?? null);
    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
      `&callback=${CALLBACK}&v=weekly&loading=async`;
    script.async = true;
    script.onerror = () => finish(null);
    document.head.appendChild(script);
  });
  return pending;
}

/** Test seam — drops the memoized load so a suite can exercise both outcomes. */
export function resetGoogleMapsLoaderForTests(): void {
  pending = null;
}
